"use client";

import { ValidatedForm } from "@/Components/ValidatedForm";

import { readText } from "@/Helpers/FormValidation";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { L, messages } from "@/libs/localization/i18n";
import { Icon } from "../icons";
import { Field, Button, Empty, Modal, Tag } from "../ui";
import { useWorkspace } from "../workspace";
import type { WorkspacePanel } from "./types";

export function WorkspacePanels({
  panel,
  onClose,
}: {
  panel: WorkspacePanel;
  onClose: () => void;
}) {
  const { locale, t, l, clients, activity, addClient, tasks } = useWorkspace();
  const router = useRouter();
  const [answer, setAnswer] = useState("");
  function assistant(
    question: "summaryQuestion" | "deadlinesQuestion" | "helpQuestion",
  ) {
    setAnswer(
      question === "summaryQuestion"
        ? l(
            L(
              "Dossier 0143: stabiel budget, 4 van de 6 schuldeisers akkoord. Het energiecontract eindigt in augustus. Bekijk de klantenkaart en de brongegevens voordat je handelt.",
              "Case 0143: stable budget, 4 of 6 creditors have agreed. The energy contract ends in August. Review the client record and source information before acting.",
            ),
          )
        : question === "deadlinesQuestion"
          ? l(
              L(
                "In het referentievoorbeeld: boedelbeschrijving 0166 over 4 dagen, R&V 0102 op 24 juli en het energiecontract van 0143 op 31 augustus.",
                "In the reference sample: inventory 0166 in 4 days, reporting for 0102 on 24 July and the energy contract for 0143 on 31 August.",
              ),
            )
          : l(
              L(
                "Filter op bron of prioriteit. Bekijk de uitleg en bronnen van een voorstel. Pas het aan, handel het zelf af of geef akkoord. Betalingen vragen altijd een tweede controle.",
                "Filter by source or priority. Review a proposal’s explanation and sources. Edit it, handle it yourself or approve it. Payments always require a second review.",
              ),
            ),
    );
  }

  return (
    <>
      {panel === "new" && (
        <Modal
          title={t("newClient")}
          description={t("demoConfirm")}
          onClose={() => onClose()}
        >
          <ValidatedForm
            onSubmit={(e) => {
              const data = new FormData(e.currentTarget);
              const name = readText(data, "name");
              if (!name || name.length > 80) return messages.validationRequired;
              const id = String(
                Math.max(...clients.map((c) => Number(c.id))) + 1,
              ).padStart(4, "0");
              addClient({
                id,
                name,
                fullName: name,
                type: data.get("type") as "guardianship",
                phase: "intake",
                handler: "Jasper K.",
                signals: 0,
              });
              onClose();
              router.push(`/${locale}/clients/${id}`);
            }}
          >
            <Field>
              {t("name")}
              <input
                name="name"
                aria-required="true"
                maxLength={80}
                autoFocus
              />
            </Field>
            <Field>
              {t("type")}
              <select name="type">
                {(
                  [
                    "guardianship",
                    "debtSupport",
                    "budgetManagement",
                    "mentoring",
                  ] as const
                ).map((type) => (
                  <option value={type} key={type}>
                    {t(type)}
                  </option>
                ))}
              </select>
            </Field>
            <div className="modal-actions flex justify-end gap-2.25 border-t border-t-line mt-5.5 pt-4.25">
              <Button onClick={() => onClose()}>{t("cancel")}</Button>
              <Button variant="primary" type="submit" icon="plus">
                {t("newClient")}
              </Button>
            </div>
          </ValidatedForm>
        </Modal>
      )}
      {panel && panel !== "new" && (
        <Modal
          title={t(panel === "audit" ? "audit" : panel)}
          description={panel === "assistant" ? t("assistantDemo") : t("demo")}
          onClose={() => onClose()}
        >
          {panel === "assistant" ? (
            <div className="assistant-content [&_>_p]:text-[12px] [&_>_p]:leading-[1.9] [&_>_p]:text-muted [&_>_p]:my-4.5 [&_>_p]:mx-0">
              <div className="assistant-orb flex items-center justify-center w-11.75 h-11.75 rounded-[14px] bg-teal-soft text-teal [&_>_.icon]:w-5.75 [&_>_.icon]:h-5.75">
                <Icon name="spark" />
              </div>
              <p>{t("assistantIntro")}</p>
              {(
                [
                  "summaryQuestion",
                  "deadlinesQuestion",
                  "helpQuestion",
                ] as const
              ).map((q) => (
                <button
                  className="assistant-question flex items-center justify-between w-full gap-3.75 text-left text-[11px] bg-[#f7fafb] border border-line p-3.25 rounded-lg mb-2.5 [&_>_.icon]:w-3.5 [&_>_.icon]:h-3.5 [&_>_.icon]:text-teal"
                  key={q}
                  onClick={() => assistant(q)}
                >
                  {t(q)}
                  <Icon name="arrow" />
                </button>
              ))}
              {answer && (
                <div
                  className="ai-box p-4.25 border border-[#cee4e7] bg-[#f0f8f9] rounded-[10px] text-[12px] leading-[1.9] mt-4.5 [&_>_a]:flex [&_>_a]:items-center [&_>_a]:gap-2 [&_>_a]:text-teal [&_>_a]:text-[11px] [&_>_a]:mt-3 [&_.icon]:w-3.25 [&_.icon]:h-3.25"
                  aria-live="polite"
                >
                  <p>{answer}</p>
                  <Link
                    href={`/${locale}/clients/0143`}
                    onClick={() => onClose()}
                  >
                    {t("client")}
                    <Icon name="arrow" />
                  </Link>
                </div>
              )}
            </div>
          ) : panel === "audit" ? (
            <div className="activity-list">
              {activity.length ? (
                activity.map((a) => (
                  <div
                    className="activity-item flex items-center gap-3 py-3.25 px-0 border-b border-b-line text-[12px] [&_small]:text-muted [&_small]:text-[10px]"
                    key={a.id}
                  >
                    <span className="activity-icon flex items-center justify-center w-7.75 h-7.75 bg-teal-soft rounded-lg text-teal flex-none [&_>_.icon]:w-3.5 [&_>_.icon]:h-3.5">
                      <Icon name="check" />
                    </span>
                    <div>
                      <p>{l(a.text)}</p>
                      <small>
                        {new Intl.DateTimeFormat(
                          locale === "nl" ? "nl-NL" : "en-GB",
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                            timeZone: "Europe/Amsterdam",
                          },
                        ).format(new Date(a.time))}
                      </small>
                    </div>
                  </div>
                ))
              ) : (
                <Empty title={t("noActivity")} />
              )}
            </div>
          ) : (
            <div className="notification-list [&_>_a]:flex [&_>_a]:items-center [&_>_a]:gap-3 [&_>_a]:py-4.25 [&_>_a]:px-0 [&_>_a]:border-b [&_>_a]:border-b-line [&_strong]:text-[12px] [&_p]:text-[10px] [&_p]:text-muted [&_p]:mt-1.25 [&_.tag]:ml-auto max-[450px]:[&_>_a]:flex-wrap">
              {tasks
                .filter((task) => task.priority === "high")
                .map((task) => (
                  <Link
                    key={task.id}
                    href={`/${locale}/work-queue`}
                    onClick={() => onClose()}
                  >
                    <span className="notification-icon flex items-center justify-center w-7.75 h-7.75 bg-teal-soft rounded-lg text-teal flex-none">
                      <Icon
                        name={task.category === "court" ? "court" : "bell"}
                      />
                    </span>
                    <div>
                      <strong>{l(task.title)}</strong>
                      <p>{task.client}</p>
                    </div>
                    <Tag tone="danger">{t("high")}</Tag>
                  </Link>
                ))}
            </div>
          )}
        </Modal>
      )}
    </>
  );
}
